import * as Sentry from '@sentry/browser';

export const PendoEvents = {
  COMPANY_BANK_CONNECTED: 'SP-CompanyBankConnected',
  USER_BANK_CONNECTED: 'SP-UserBankConnected',
  CARD_ISSUED: 'SP-CardIssued',
  USE_SPEND_UI: 'AB-SwitchToSpendUI',
  USE_ABACUS_UI: 'AB-SwitchToAbaUI',
  PAGE_OPEN_ADMIN_DASHBOARD: 'SP-PageOpenedAdminDashboard',
  PAGE_OPEN_USER_DASHBOARD: 'SP-PageOpenedUserDashboard',
  PAGE_OPEN_EXPENSE_SEARCH: 'AB-ExpenseSearch',
  HEADER_LOADED: 'SP-Page-Load',
  ONBOARDING_EXPERIENCE_SURVEY: 'SP-Onboarding-Exp-Survey-Nov2024',
  SIGNUP_FLOW_EMAIL_VERIFICATION: 'SP-SignupFlow-EmailVerification',
  SIGNUP_FLOW_COMPANY_INFO: 'SP-SignupFlow-CompanyInfo',
  SIGNUP_FLOW_PERSONAL_INFO: 'SP-SignupFlow-PersonalInfo',
  SIGNUP_FLOW_PLAN_SELECTION: 'SP-SignupFlow-PlanSelection',
  SIGNUP_FLOW_IMPLEMENTATION_OPTIONS: 'SP-SignupFlow-ImplementationOptions',
  SIGNUP_FLOW_EMBURSE_CARDS: 'SP-SignupFlow-EmburseCards',
  SIGNUP_FLOW_BUSINESS_VERIFICATION: 'SP-SignupFlow-BusinessVerification',
  SIGNUP_FLOW_REFERRAL_SCREEN: 'SP-SignupFlow-ReferralScreen',
  SIGNUP_FLOW_SUBMIT_APPLICATION: 'SP-SignupFlow-SubmitApplication',
  SIGNUP_FLOW_PAYMENT_SUMMARY: 'SP-SignupFlow-PaymentSummary',
  SIGNUP_FLOW_FINISHED: 'SP-SignupFlow-Finished',
  ANNUAL_UPGRADE_FLOW_INITIATED: 'SP-AnnualUpgradeFlow-Initiated',
  ANNUAL_UPGRADE_FLOW_TAB_SWITCHED: 'SP-AnnualUpgradeFlow-TabSwitched',
  ANNUAL_UPGRADE_FLOW_CHANGE_USER_BAND: 'SP-AnnualUpgradeFlow-ChangeUserBand',
  ANNUAL_UPGRADE_FLOW_CHECKOUT_INITIATED: 'SP-AnnualUpgradeFlow-CheckoutInitiated',
  ANNUAL_UPGRADE_FLOW_COMPLETED: 'SP-AnnualUpgradeFlow-Completed'
} as const;

export const PendoGuides = {
  DISCONNECTED_CARD_PROGRAMS: 'XWUIlnKadxxFWERX-KsTU6V5M_w', //TODO: cleanup this pendo guide as this appears on the dashboard task list now
  ONBOARDING_EXPERIENCE_SURVEY: '1AFTXvVxxA_QOTUhAQPJ9KjAtAI'
} as const;

export const PendoGuideSessionStorageKey = 'pendo-guide-shown';

const PENDO_GUIDE_MAX_CONNECTION_ATTEMPTS = 10;

export abstract class PendoService {
  static pendoEnabled = window.FEATURES?.pendo;

  static trackEvent(eventName: typeof PendoEvents[keyof typeof PendoEvents], payload?: any) {
    if (PendoService.pendoEnabled) {
      PendoService.runMethodWhenPendoIsReady((pendoInstance) => {
        pendoInstance.track(eventName, payload);
      });
    }
  }

  static findAndShowGuide(guideId: typeof PendoGuides[keyof typeof PendoGuides], forceShow = false) {
    //we want to show the guide only once per session
    if (sessionStorage[PendoGuideSessionStorageKey + guideId] === 'true' && !forceShow) {
      return false;
    }

    if (PendoService.pendoEnabled) {
      PendoService.runMethodWhenPendoIsReady((pendoInstance) => {
        const guide = pendoInstance.findGuideById(guideId);
        if (guide) {
          guide.show();
        } else {
          Sentry.captureMessage(`Pendo Guide not found: ${guideId}`);
        }
      });
      sessionStorage[PendoGuideSessionStorageKey + guideId] = 'true';
    }
  }

  private static runMethodWhenPendoIsReady(method: (pendoInstance: any) => void) {
    const pendoInstance = window.pendo;

    let attemptNumber = 0;

    function runMethodWhenReady() {
      if (pendoInstance) {
        if (pendoInstance.isReady && pendoInstance.isReady() && pendoInstance.guides) {
          method(pendoInstance);
        } else {
          // It takes some time for pendo to initialize, so we retry a maximum of PENDO_GUIDE_MAX_CONNECTION_ATTEMPTS times,
          // with increasing intervals
          if (attemptNumber < PENDO_GUIDE_MAX_CONNECTION_ATTEMPTS) {
            attemptNumber++;
            setTimeout(() => runMethodWhenReady(), 1000 * attemptNumber);
          } else {
            Sentry.captureMessage(`Pendo Instance not found after ${PENDO_GUIDE_MAX_CONNECTION_ATTEMPTS} attempts.`);
          }
        }
      }
    }

    if (PendoService.pendoEnabled) {
      runMethodWhenReady();
    }
  }
}
